import { SHOW_LAD_SENSOR_MIN_ZOOM } from '@utils/map/mapZoom.constants';
import { SENSOR_LABEL_TEXT_SIZE } from '@utils/sensors/configuration.constants';
import { CircleLayerSpecification, SymbolLayerSpecification } from 'mapbox-gl';

import { FeatureTypeEnum, LayerNameEnum, SourceNameEnum } from '@/types/map.types';
import { SENSOR_LABEL_ICON_OFFSET_PX, SENSOR_LABEL_TEXT_OFFSET_PX_L } from '@/types/sensor/sensor.types';

export enum ClusterColor {
  PURPLE = 'PURPLE',
  ORANGE = 'ORANGE',
  GREEN = 'GREEN',
  ORANGE_MAINTENANCE = 'ORANGE_MAINTENANCE',
  GRAY = 'GRAY',
  WHITE = 'WHITE',
  RED = 'RED',
}

export const clustersIconLayer: SymbolLayerSpecification = {
  id: LayerNameEnum.SENSOR_CLUSTERS,
  type: 'symbol',
  source: SourceNameEnum.SENSORS,
  filter: ['all', ['has', 'cluster'], ['==', ['coalesce', ['get', 'selected'], false], false]],
  layout: {
    'text-field': [
      'case',
      [
        'any',
        ['>', ['coalesce', ['get', ClusterColor.PURPLE], 0], 0],
        ['>', ['coalesce', ['get', ClusterColor.ORANGE], 0], 0],
        ['>', ['coalesce', ['get', ClusterColor.RED], 0], 0],
        ['>', ['coalesce', ['get', ClusterColor.GREEN], 0], 0],
        ['>', ['coalesce', ['get', ClusterColor.ORANGE_MAINTENANCE], 0], 0],
        ['>', ['coalesce', ['get', ClusterColor.GRAY], 0], 0],
        ['>', ['coalesce', ['get', ClusterColor.WHITE], 0], 0],
      ],
      ['to-string', ['get', 'point_count']],
      '',
    ],
    'text-font': ['roboto'],
    'text-size': 24,
    'text-offset': [0, 0.1],
    'text-ignore-placement': true,
    'text-allow-overlap': true,
    'icon-ignore-placement': true,
    'icon-allow-overlap': true,
    'icon-anchor': 'center',
    'icon-image': [
      'case',
      ['>', ['coalesce', ['get', ClusterColor.PURPLE], 0], 0],
      'icSymb-cluster-pr',
      ['>', ['coalesce', ['get', ClusterColor.ORANGE], 0], 0],
      'icSymb-cluster-or',
      ['>', ['coalesce', ['get', ClusterColor.RED], 0], 0],
      'icSymb-cluster-rd',
      ['>', ['coalesce', ['get', ClusterColor.GREEN], 0], 0],
      'icSymb-cluster-gn',
      ['>', ['coalesce', ['get', ClusterColor.ORANGE_MAINTENANCE], 0], 0],
      'icSymb-cluster-or',
      ['>', ['coalesce', ['get', ClusterColor.GRAY], 0], 0],
      'icSymb-cluster-gy',
      ['>', ['coalesce', ['get', ClusterColor.WHITE], 0], 0],
      'icSymb-cluster-wh',
      'icSymb-cluster-wh',
    ],
  },
};

export const clusterOverlay: CircleLayerSpecification = {
  id: LayerNameEnum.SENSOR_CLUSTER_OVERLAY,
  type: 'circle',
  source: SourceNameEnum.SENSORS,
  filter: ['all', ['has', 'cluster'], ['==', ['get', 'selected'], true]],
  minzoom: 13,
  maxzoom: 23,
  paint: {
    'circle-color': 'black',
    'circle-opacity': 0.5,
    'circle-radius': ['coalesce', ['get', 'clusterRadius'], 48],
    'circle-stroke-width': 2,
    'circle-stroke-color': 'white',
  },
};

export const clusterLeavesLayer: SymbolLayerSpecification = {
  id: LayerNameEnum.SENSOR_CLUSTER_LEAVES,
  type: 'symbol',
  source: SourceNameEnum.SENSORS,
  filter: ['all', ['==', ['get', 'featureType'], FeatureTypeEnum.SENSOR_ICON], ['has', 'clusterFeature']],
  minzoom: 13,
  maxzoom: 23,
  layout: {
    'icon-image': ['concat', ['get', 'baseIcon'], '-L'],
    'icon-size': 1,
    'icon-anchor': 'center',
    'icon-allow-overlap': true,
    'icon-ignore-placement': true,
    'text-allow-overlap': true,
    'text-ignore-placement': true,
    'symbol-z-order': 'viewport-y',
    'symbol-avoid-edges': true,
  },
};

export const sensorIconLayer: SymbolLayerSpecification = {
  id: LayerNameEnum.SENSOR_ICONS,
  type: 'symbol',
  source: SourceNameEnum.SENSORS,
  filter: [
    'all',
    ['==', ['get', 'featureType'], FeatureTypeEnum.SENSOR_ICON],
    ['!', ['has', 'cluster']],
    ['!', ['has', 'point_count']],
    ['!', ['has', 'clusterFeature']],
  ],
  layout: {
    'icon-image': ['step', ['zoom'], ['get', 'iconS'], SHOW_LAD_SENSOR_MIN_ZOOM, ['get', 'iconL']],
    'icon-size': 1,
    'icon-anchor': 'left',
    'icon-offset': ['step', ['zoom'], [-14, 0], SHOW_LAD_SENSOR_MIN_ZOOM, [-34, 0]],
    'icon-allow-overlap': true,
    'icon-ignore-placement': true,
    'symbol-z-order': 'source',
  },
};

export const selectedSensorIconLayer: SymbolLayerSpecification = {
  ...sensorIconLayer,
  id: LayerNameEnum.SENSOR_ICON_SELECTION,
  source: SourceNameEnum.SELECTION,
  filter: ['==', ['get', 'featureType'], FeatureTypeEnum.SENSOR_ICON],
  layout: {
    ...sensorIconLayer.layout,
    'icon-offset': ['step', ['zoom'], [-33, 0], SHOW_LAD_SENSOR_MIN_ZOOM, [-55, 0]],
    'icon-image': [
      'step',
      ['zoom'],
      [
        'case',
        ['==', ['coalesce', ['get', 'openedCluster', ['get', 'value']], false], true],
        ['concat', ['get', 'iconL'], '-halo'],
        ['concat', ['get', 'iconS'], '-halo'],
      ],
      SHOW_LAD_SENSOR_MIN_ZOOM,
      ['concat', ['get', 'iconL'], '-halo'],
    ],
  },
};

export const sensorIconLabelLayer: SymbolLayerSpecification = {
  id: LayerNameEnum.SENSOR_LABELS,
  type: 'symbol',
  source: SourceNameEnum.SENSORS,
  filter: ['==', ['get', 'featureType'], FeatureTypeEnum.SENSOR_LABEL],
  minzoom: SHOW_LAD_SENSOR_MIN_ZOOM,
  layout: {
    'icon-image': 'icSymb-label',
    'icon-size': 1,
    'icon-anchor': 'center',
    'icon-allow-overlap': true,
    'icon-ignore-placement': true,
    'icon-offset': SENSOR_LABEL_ICON_OFFSET_PX,
    'text-font': ['roboto'],
    'text-field': ['get', 'textField'],
    'text-size': SENSOR_LABEL_TEXT_SIZE,
    'text-allow-overlap': true,
    'text-ignore-placement': true,
    'text-offset': SENSOR_LABEL_TEXT_OFFSET_PX_L,
    'symbol-z-order': 'source',
  },
  paint: {
    'text-color': 'white',
  },
};

export const selectedSensorIconLabelLayer: SymbolLayerSpecification = {
  ...sensorIconLabelLayer,
  id: LayerNameEnum.SENSOR_LABEL_SELECTION,
  source: SourceNameEnum.SELECTION,
};
